// modelli
import { SearchMode } from '../interfaces/mapSearch.interface';
import { LayerVector, LayerWMS, LayerWMTS, LayerWorld, LayerXYZ } from './layer.model';
import { FeatureStyle } from './style.model';

/**
 * Interfaccia per strutturare la configurazione per la costruzione della mappa
 */
export interface IMapConfig {
  /**
   * Configurazione iniziale della mappa.
   *
   * Coordinate [longitudine, latitudine]
   */
  mapSetup: {
    coordinate: number[];
    zoom: number;
    zoomMin: number;
    zoomMax: number;
    clickSize: number;
  };

  /**
   * Configurazione della preview
   *
   * previewZoomDifference --> differenza di zoom tra la preview e la mappa utente
   *
   * style --> Id dello style da utilizzare per rappresentare l'area di lavoro della mappa utente
   *
   * height --> height in px della preview
   *
   * width --> width in px della preview
   */
  mapPreviewSetup: {
    previewZoomDifference: number;
    style: string;
    height: number;
    width: number;
    position?: 'bottomLeft' | 'topRight';
  };

  proxyURL: string;

  mapProjections: {
    code: string;
    proj: string;
    extent: [number, number, number, number];
    info: {
      description: string;
      name: string;
      datum: string;
      projection: string;
      zone: string;
    };
  }[];

  /**
   * Configurazione accesso ai servizi GoogleMaps
   */
  google_key?: string;

  /**
   * Array di layers di terze parti (OSM, Google, ...)
   */
  worldLayers: LayerWorld[];

  /**
   * Array di layers WMS
   */
  wmsLayers: LayerWMS[];

  /**
   * Array di layers WMTS
   */
  wmtsLayers: LayerWMTS[];

  /**
   * Array di layers XYZ
   */
  xyzLayers: LayerXYZ[];

  /**
   * Array di layers vettoriali
   */
  vectorLayers: LayerVector[];

  /**
   * Array di stili per i layers
   */
  styles: FeatureStyle[];

  /**
   * Configurazione iniziale scelta rappresentazione
   */
  sceltaRappresentazione: {
    tab_id: string;
    tab_name: string;
    visibility: string[];
    title: string;
    content: {
      group_name: string;
      title: string;
      type: string;
      values: string[];
      labels: string[];
      legend?: string[];
      checked: any;
      enabledBy?: string[];
      enableOperator?: string;
    }[];
  }[];

  /**
   * Lista di dataset disponibili per query
   */
  datasets: {
    name: string;
    label: string;
    type: string;
    queryable?: boolean;
  }[];

  /*
   * Configurazione per il centramento della mappa
   */
  centraMappa: {
    zoom: number;
    icon: string;
    timeout: number;
  };

  //Search mode
  searchSetup: ISearchConfig;

  mapLayout: IMapLayout;

  layerOrder?: string[];

  staticStyles?: string[];
}

export interface IShapeConfig {
  canAddStyle: boolean;
  canUpdateStyle: boolean;
  canDeleteStyle: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface IMapLayout {
  //Basemap change control
  worldLayerChange: {
    active: boolean;
  };

  //The small map preview
  mapPreview: {
    active: boolean;
  };

  //The view of mouse coordinates and resolution
  infoView: {
    active: boolean;
  };

  //The feature select functionality where the click fetches data from the API about the added layers
  featureSelect?: {
    active: boolean;
  };

  //Google search input
  googleSearch: {
    active: boolean;
  };

  //Google Street View control and component
  googleStreetView: {
    active: boolean;
  };

  //Zoom-in and zoom-out controls
  zoomControls: {
    active: boolean;
  };

  //Full screen control
  fullScreenControl: {
    active: boolean;
  };

  //The user location control and functionality
  goToPositionControl: {
    active: boolean;
  };

  //The legend control
  legend: {
    active: boolean;
  };

  //Layer selection and visibility
  layerSelection: {
    active: boolean;
  };

  //Tool for drawing different geometries
  drawingTool: {
    active: boolean;
  };

  //Tool for transforming coordinates between coordinate systems
  transformCoordinates: {
    active: boolean;
  };

  //Tool for manager layers
  dynamic_layers: {
    active: boolean;
  };

  //Tool for manager shape layer
  shape_layers: {
    active: boolean;
  };

  //Tool for manager query
  query: {
    active: boolean;
  };
  //Tool for manager bookmark
  bookmark: {
    active: boolean;
  };
  //Tool for view valves
  valves: {
    active: boolean;
  };
  //Tool for view otdr
  otdr: {
    active: boolean;
  };
  //Tool for view psa
  psa: {
    active: boolean;
  };
  //Tool for upload/validate/save PSA controlli analitici (prelievi, superamenti Reportnet3)
  controlli?: {
    active: boolean;
  };
  //Tool for transmitting PSA controlli/superamenti data to the AnTeA portal
  trasmissioneAntea?: {
    active: boolean;
  };
  //Tool for view checkInOut
  checkInOut: {
    active: boolean;
  };
  //Tool for view analisi schiacciamenti
  analisiSchiacciamenti: {
    active: boolean;
  };
  //Tool for tracing
  tracing: {
    active: boolean;
  };
  //Tool for checkInAnalysis
  checkInAnalysis: {
    active: boolean;
  };
  fiberJoints: {
    active: boolean;
  };
  toolFattibilita: {
    active: boolean;
  };
}

export interface ISearchConfig {
  modes: SearchMode[];
}
